import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Atom, Divide, Sigma, Infinity, BrainCircuit, Globe, ArrowRight, Share2 } from 'lucide-react';

const QuantumPhysics: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Bohr Institute";
    
    return (
        <div className="min-h-screen bg-slate-50 font-serif text-slate-800 selection:bg-blue-200 selection:text-blue-900 border-[20px] border-white overflow-x-hidden">
            
            {/* Background Formulas (Decorative) */}
            <div className="fixed top-20 left-10 text-9xl text-slate-200 font-serif italic pointer-events-none opacity-50 z-0">
                &psi;
            </div>
            <div className="fixed bottom-20 right-10 text-9xl text-slate-200 font-serif italic pointer-events-none opacity-50 z-0">
                &#8463;
            </div>
             <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-slate-200 rounded-full opacity-30 pointer-events-none z-0"></div>
             <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-dotted border-slate-300 rounded-full opacity-30 pointer-events-none z-0 animate-[spin_60s_linear_infinite]"></div>


            {/* Top Bar for Academic Credibility */}
            <div className="relative z-10 bg-slate-900 text-slate-400 text-[10px] uppercase tracking-widest py-2 px-12 flex justify-between">
                <span>Est. 1922</span>
                <span>Department of Theoretical Physics</span>
            </div>

            {/* Navbar */}
            <nav className="relative z-10 px-12 py-12 flex justify-between items-center bg-transparent">
                <div className="flex items-center gap-4">
                     <Atom size={42} strokeWidth={1} className="text-slate-900" />
                     <div className="flex flex-col">
                         <span className="font-bold text-2xl tracking-tight text-slate-900 leading-none">{schoolName}</span>
                         <span className="text-xs font-sans text-slate-500 uppercase tracking-widest mt-1">Research & Education</span>
                     </div>
                </div>

                <div className="hidden md:flex gap-12 font-sans text-sm font-medium text-slate-600">
                    <a href="#" className="hover:text-blue-600 transition-colors">Faculty</a>
                    <a href="#" className="hover:text-blue-600 transition-colors">Publications</a>
                    <a href="#" className="hover:text-blue-600 transition-colors">Laboratories</a>
                    <a href="#" className="hover:text-blue-600 transition-colors">Events</a>
                </div>

                <Button variant="outline" className="font-sans rounded-full border-slate-300 hover:border-slate-900 hover:bg-slate-50 px-8">
                    Portal Login
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 px-12 py-20 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                    <div>
                        <h1 className="text-6xl md:text-8xl font-medium text-slate-900 mb-12 leading-[0.9]">
                            Unlocking the <br/>
                            <span className="italic text-blue-600">Fabric</span> of <br/>
                            Reality.
                        </h1>
                        <p className="font-sans text-lg text-slate-500 leading-relaxed max-w-md mb-12 border-l-2 border-slate-200 pl-6">
                            From quantum entanglement to super-conductors, we push the boundaries of human understanding through rigorous mathematical frameworks and experimental validation.
                        </p>
                        
                        <div className="flex gap-4 font-sans">
                            <Button className="bg-slate-900 text-white rounded-none h-14 px-8 text-sm uppercase tracking-wider hover:bg-blue-700 transition-colors">
                                Apply for PhD
                            </Button>
                            <Button variant="ghost" className="text-slate-900 hover:bg-slate-100 rounded-none h-14 px-8 text-sm uppercase tracking-wider gap-2">
                                Read Journal <ArrowRight size={16} />
                            </Button>
                        </div>
                    </div>

                    {/* Scientific Diagram */}
                    <div className="relative aspect-square border-2 border-slate-200 p-8 flex items-center justify-center bg-white shadow-xl">
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                        
                        {/* Central Graphic */}
                        <div className="relative z-10 w-64 h-64">
                            <div className="absolute inset-0 border border-blue-200 rounded-full animate-[ping_3s_ease-out_infinite]"></div>
                            <div className="absolute inset-4 border border-blue-300 rounded-full animate-[spin_10s_linear_infinite]"></div>
                            <div className="absolute inset-8 border border-blue-400 rounded-full animate-[spin_8s_linear_infinite_reverse]"></div>
                            <div className="absolute top-1/2 left-1/2 w-4 h-4 bg-slate-900 rounded-full transform -translate-x-1/2 -translate-y-1/2 z-20 shadow-lg"></div>
                            
                            {/* Particle trails */}
                            <div className="absolute top-1/2 left-1/2 w-32 h-[1px] bg-slate-900 origin-left rotate-45"></div>
                            <div className="absolute top-1/2 left-1/2 w-32 h-[1px] bg-slate-900 origin-left rotate-[135deg]"></div>
                            <div className="absolute top-1/2 left-1/2 w-32 h-[1px] bg-slate-900 origin-left rotate-[270deg]"></div>
                            
                            <div className="absolute -right-12 top-0 bg-white border border-slate-200 p-2 text-xs font-mono shadow-sm">
                                E = h&nu;
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Latest Findings / Cards */}
            <section className="relative z-10 bg-slate-100 py-24 px-12 border-t border-slate-200">
                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-between items-end mb-16">
                        <h2 className="text-4xl text-slate-900 font-medium">Research Areas</h2>
                        <a href="#" className="font-sans text-sm font-bold text-blue-600 uppercase tracking-widest hover:underline">View All Papers</a>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                         <div className="bg-white p-10 shadow-sm border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all">
                             <BrainCircuit size={32} strokeWidth={1} className="text-blue-600 mb-6" />
                             <h3 className="font-sans text-lg font-bold text-slate-900 uppercase tracking-wide mb-4">Quantum Computing</h3>
                             <p className="font-sans text-slate-500 leading-relaxed text-sm">Developing stable qubits for next-generation processing power.</p>
                         </div>
                         <div className="bg-white p-10 shadow-sm border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all">
                             <Infinity size={32} strokeWidth={1} className="text-blue-600 mb-6" />
                             <h3 className="font-sans text-lg font-bold text-slate-900 uppercase tracking-wide mb-4">String Theory</h3>
                             <p className="font-sans text-slate-500 leading-relaxed text-sm">Unifying gravity and quantum mechanics in higher dimensional space.</p>
                         </div>
                         <div className="bg-white p-10 shadow-sm border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all">
                             <Globe size={32} strokeWidth={1} className="text-blue-600 mb-6" />
                             <h3 className="font-sans text-lg font-bold text-slate-900 uppercase tracking-wide mb-4">Particle Physics</h3>
                             <p className="font-sans text-slate-500 leading-relaxed text-sm">Smashing particles to discover the fundamental building blocks of matter.</p>
                         </div>
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-slate-900 text-slate-400 py-20 px-12 font-sans text-sm">
                 <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
                     <div>
                         <div className="flex items-center gap-2 mb-4">
                             <Atom size={24} className="text-white" />
                             <span className="font-bold text-white text-lg tracking-tight">{schoolName}</span>
                         </div>
                         <p className="max-w-xs leading-relaxed">
                             Dedicated to the pursuit of knowledge and the understanding of the universe.
                         </p>
                     </div>
                     <div className="flex gap-8 mt-8 md:mt-0">
                         <a href="#" className="hover:text-white transition-colors">Library</a>
                         <a href="#" className="hover:text-white transition-colors">Contact</a>
                         <a href="#" className="hover:text-white transition-colors">Donate</a>
                         <div className="flex items-center gap-2 text-white border-l border-white/20 pl-8">
                             <Share2 size={16} /> Connect
                         </div>
                     </div>
                 </div>
            </footer>
        </div>
    );
};

export default QuantumPhysics;
